// Tenant resolver — maps the Teams getContext().tenantId (an AAD
// tenant GUID) onto a per-tenant ACA backend URL.
//
// The mapping lives in `public/tenants.json`, served alongside the SPA
// bundle. Adding a new customer = appending one line to tenants.json
// (a Variables-layer flip, per F37 spec §3 productization framing).
// No code change.
//
// The frontend never holds backend secrets: the tenants.json file maps
// `tenantId → backendUrl`, and the backend itself is responsible for
// authenticating the bearer token. The mapping being client-side public
// is acceptable — the URLs are not secrets, the AAD-verified token is
// what proves the caller's identity.

import type { Language } from "./i18n";

/** One entry in `public/tenants.json`. */
export interface TenantEntry {
  /** Per-customer ACA backend FQDN (https://kj-<slug>-...). */
  backendUrl: string;
  /** Short slug for telemetry / activity-log correlation; no security
   * boundary. */
  slug: string;
  /** Per-tenant language preference. Drives PT-AO + EN selection on
   * the SPA side; the backend independently respects its own tenant
   * config. */
  language: Language;
}

/** Shape of `public/tenants.json`. Key = AAD tenant GUID; value =
 * the per-tenant record. */
export type TenantsManifest = Record<string, TenantEntry>;

export class UnknownTenantError extends Error {
  constructor(public readonly tenantId: string) {
    super(`Unknown tenant: ${tenantId}`);
    this.name = "UnknownTenantError";
  }
}

/** Fetch the tenants manifest. Cached for the lifetime of the SPA
 * session — the manifest changes on deploy, not at runtime, and the
 * tab reloads every time Teams restarts. */
let _manifestPromise: Promise<TenantsManifest> | null = null;

export async function loadTenantsManifest(
  fetchImpl: typeof fetch = fetch,
): Promise<TenantsManifest> {
  if (_manifestPromise) return _manifestPromise;
  _manifestPromise = (async () => {
    const response = await fetchImpl("/tenants.json", {
      cache: "no-cache",
    });
    if (!response.ok) {
      throw new Error(
        `tenants.json fetch failed: ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()) as TenantsManifest;
  })();
  return _manifestPromise;
}

/** Resolve the per-tenant entry for `tenantId`. Throws
 * `UnknownTenantError` if no entry is registered. */
export async function resolveTenant(
  tenantId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TenantEntry> {
  const manifest = await loadTenantsManifest(fetchImpl);
  const entry = manifest[tenantId];
  if (!entry) {
    throw new UnknownTenantError(tenantId);
  }
  return entry;
}

/** Test-only reset. Production code never resets — the manifest is
 * loaded once per SPA session. */
export function _resetForTest(): void {
  _manifestPromise = null;
}
